import { isSoldOut } from '@/lib/stock';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { ShopifyProduct, createStorefrontCheckout, fetchProductByHandle } from '@/lib/shopify';
import { GIFT_PROMO } from '@/config/giftPromo';

import { trackAddToCart, trackBeginCheckout } from '@/lib/eventDispatcher';
import { trackAddToCart as analyticsAddToCart } from '@/lib/analytics';
import { dlAddToCart, dlRemoveFromCart, dlBeginCheckout, shopifyNodeToItem, shopifyNumericId } from '@/lib/dataLayer';
import { mpAddToCart, mpInitiateCheckout } from '@/lib/metaPixel';
import { sendMetaCAPI } from '@/lib/metaCapi';

// Variant IDs that already emitted a GA4 `add_to_cart` in THIS browser session.
// Reset on full page reload (module scope), NOT persisted. Used so that a
// rehydrated/persisted cart — or any direct-checkout path — still fires
// `add_to_cart` before `begin_checkout`, without double-counting items that
// were normally added in the same session. Fixes GA4 begin_checkout > add_to_cart.
const sessionTrackedAtc = new Set<string>();


export interface CartItem {
  product: ShopifyProduct;
  variantId: string;
  variantTitle: string;
  price: {
    amount: string;
    currencyCode: string;
  };
  quantity: number;
  selectedOptions: Array<{
    name: string;
    value: string;
  }>;
  source?: string;
  isGift?: boolean;
}

interface CartStore {
  items: CartItem[];
  cartId: string | null;
  checkoutUrl: string | null;
  isLoading: boolean;
  isCartOpen: boolean;
  
  // Actions
  addItem: (item: CartItem) => void;
  updateQuantity: (variantId: string, quantity: number) => void;
  removeItem: (variantId: string) => void;
  reconcileGift: () => void;
  clearCart: () => void;
  setCartId: (cartId: string) => void;
  setCheckoutUrl: (url: string) => void;
  setLoading: (loading: boolean) => void;
  setIsCartOpen: (open: boolean) => void;
  createCheckout: () => Promise<string | null>;
  getSubtotal: () => number;
  getItemCount: () => number;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      cartId: null,
      checkoutUrl: null,
      isLoading: false,
      isCartOpen: false,

      addItem: (item) => {
        // Sold-out variants can never enter the cart (any caller).
        if (isSoldOut(item.product?.node, item.variantId)) {
          console.warn('[cart] blocked sold-out variant', item.variantId);
          return;
        }
        // ── 1) CART MUTATION FIRST ──────────────────────────────────────────
        // The cart update + drawer open MUST happen before ANY tracking call so
        // that a third-party script failing (fbq/gtag/dataLayer not loaded, common
        // in the Instagram/Messenger in-app browser) can NEVER block add-to-cart.
        // This was the root cause of "dead clicks" on /loja: tracking threw before
        // the drawer opened, so the click looked dead even though state changed.
        const { items } = get();
        const existingItem = items.find(i => i.variantId === item.variantId);

        if (existingItem) {
          set({
            items: items.map(i =>
              i.variantId === item.variantId
                ? { ...i, quantity: i.quantity + item.quantity }
                : i
            )
          });
        } else {
          set({ items: [...items, item] });
        }

        // Open drawer + reconcile gift IMMEDIATELY (never gated by tracking).
        set({ isCartOpen: true });
        get().reconcileGift();

        // ── 2) TRACKING (fully isolated, never throws into the caller) ───────
        try {
          // Track on EVERY add_to_cart (new or quantity increase) — GA4 spec
          trackAddToCart(
            item.variantId,
            item.quantity,
            parseFloat(item.price.amount),
            item.product.node.title
          );
          analyticsAddToCart({
            sku: item.variantId,
            productName: item.product.node.title,
            price: parseFloat(item.price.amount),
            handle: item.product.node.handle,
          });
          // Shared event_id for Pixel <-> CAPI dedup (AddToCart) — generated
          // BEFORE dlAddToCart so the same ID reaches the dataLayer, the
          // Pixel and the CAPI mirror.
          const addToCartEventId = `addtocart_${item.variantId}_${Date.now()}`;
          // GTM dataLayer — GA4 ecommerce add_to_cart
          dlAddToCart([shopifyNodeToItem(
            item.product.node,
            parseFloat(item.price.amount),
            item.quantity,
            item.source,
            undefined,
            item.variantId,
          )], addToCartEventId);
          // Mark this variant as add_to_cart-tracked for the current session so
          // the checkout chokepoint below won't re-emit it.
          sessionTrackedAtc.add(item.variantId);
          // Meta Pixel AddToCart
          mpAddToCart({
            content_ids: [shopifyNumericId(item.variantId)],
            content_name: item.product.node.title,
            value: parseFloat(item.price.amount) * item.quantity,
            event_id: addToCartEventId,
          });
          // Server-side CAPI for AddToCart (fire-and-forget, with retry + structured logs)
          void sendMetaCAPI({
            event_name: 'AddToCart',
            event_id: addToCartEventId,
            custom_data: {
              value: parseFloat(item.price.amount) * item.quantity,
              currency: 'BRL',
              content_ids: [shopifyNumericId(item.variantId)],
              content_name: item.product.node.title,
              ...(item.source ? { content_category: item.source } : {}),
            },
          });
        } catch (err) {
          if (import.meta.env.DEV) console.warn('[Cart] add_to_cart tracking failed (non-blocking):', err);
        }
      },

      // Gift-with-purchase reconciliation. Adds/removes the free gift line
      // directly via set() so it NEVER fires any analytics event (the gift has
      // value 0 and must not pollute AddToCart/begin_checkout/Purchase).
      reconcileGift: () => {
        // Promotion disabled: strip any gift line and stop.
        if (!GIFT_PROMO.enabled) {
          const { items } = get();
          if (items.some(i => i.isGift)) {
            set({ items: items.filter(i => !i.isGift) });
          }
          return;
        }

        const { items } = get();
        const hasTrigger = items.some(
          i => i.variantId === GIFT_PROMO.triggerVariantId && i.quantity > 0 && !i.isGift,
        );
        const giftIndex = items.findIndex(
          i => i.isGift === true || i.variantId === GIFT_PROMO.giftVariantId,
        );
        const hasGift = giftIndex !== -1;

        // Trigger present but gift missing → fetch and add gift line.
        if (hasTrigger && !hasGift) {
          void (async () => {
            try {
              const product = await fetchProductByHandle(GIFT_PROMO.giftHandle);
              const firstVariant = product?.variants?.edges?.[0]?.node;
              if (!product || !firstVariant || firstVariant.availableForSale === false) {
                return; // out of stock / not found → silent no-op
              }
              const current = get().items;
              // Re-check: another reconcile may have added it meanwhile.
              if (current.some(i => i.isGift === true || i.variantId === GIFT_PROMO.giftVariantId)) {
                return;
              }
              if (!current.some(i => i.variantId === GIFT_PROMO.triggerVariantId && i.quantity > 0 && !i.isGift)) {
                return; // trigger removed while fetching
              }
              const giftItem: CartItem = {
                product: { node: product },
                variantId: firstVariant.id,
                variantTitle: firstVariant.title,
                price: { amount: '0.00', currencyCode: 'BRL' },
                quantity: 1,
                selectedOptions: firstVariant.selectedOptions || [],
                source: 'brinde',
                isGift: true,
              };
              set({ items: [...current, giftItem] });
            } catch (e) {
              if (import.meta.env.DEV) console.warn('[GiftPromo] reconcile failed:', e);
            }
          })();
          return;
        }

        // Trigger absent but gift present → remove gift line.
        if (!hasTrigger && hasGift) {
          set({ items: items.filter((_, idx) => idx !== giftIndex) });
          return;
        }

        // Gift present with wrong quantity → force back to maxGiftQty.
        if (hasGift && items[giftIndex].quantity !== GIFT_PROMO.maxGiftQty) {
          set({
            items: items.map((i, idx) =>
              idx === giftIndex ? { ...i, quantity: GIFT_PROMO.maxGiftQty } : i,
            ),
          });
        }
      },


      updateQuantity: (variantId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(variantId);
          return;
        }
        
        set({
          items: get().items.map(item =>
            item.variantId === variantId ? { ...item, quantity } : item
          )
        });
        get().reconcileGift();
      },

      removeItem: (variantId) => {
        const removed = get().items.find(item => item.variantId === variantId);
        set({
          items: get().items.filter(item => item.variantId !== variantId)
        });
        if (removed) {
          dlRemoveFromCart([shopifyNodeToItem(
            removed.product.node,
            parseFloat(removed.price.amount),
            removed.quantity,
            undefined,
            undefined,
            removed.variantId,
          )]);
        }
        get().reconcileGift();
      },

      clearCart: () => {
        set({ items: [], cartId: null, checkoutUrl: null });
      },

      setCartId: (cartId) => set({ cartId }),
      setCheckoutUrl: (checkoutUrl) => set({ checkoutUrl }),
      setLoading: (isLoading) => set({ isLoading }),
      setIsCartOpen: (isCartOpen) => set({ isCartOpen }),

      createCheckout: async () => {
        const { items, setLoading, setCheckoutUrl, getSubtotal } = get();
        if (items.length === 0) return null;

        setLoading(true);
        try {
          // Exclude gift (isGift) lines from the Shopify payload. The 100%-off
          // gift is added automatically by Shopify's Buy-X-Get-Y discount at
          // checkout — sending it ourselves would DUPLICATE the line (the bug
          // where the gift/necessaire appeared twice). We keep it visible in the
          // local drawer for UX, but Shopify is the single source of truth at checkout.
          const checkoutItems = items
            .filter(item => !item.isGift)
            .map(item => ({
              variantId: item.variantId,
              quantity: item.quantity,
            }));
          const checkoutUrl = await createStorefrontCheckout(checkoutItems);
          if (checkoutUrl) {
            setCheckoutUrl(checkoutUrl);

            // ── add_to_cart SAFETY NET (fixes GA4 begin_checkout > add_to_cart) ──
            // Every path that starts checkout funnels through here (cart drawer,
            // PDP "Comprar agora", quick-buy, kits/bundles). If any item in the
            // cart never emitted add_to_cart this session — most commonly a
            // rehydrated/persisted cart from a previous visit — emit it NOW,
            // BEFORE begin_checkout, with value/currency BRL and items. Deduped
            // per session so normally-added items are not double-counted.
            try {
              const untracked = items.filter(
                i => !i.isGift && !sessionTrackedAtc.has(i.variantId),
              );
              if (untracked.length > 0) {
                dlAddToCart(
                  untracked.map(i => shopifyNodeToItem(
                    i.product.node,
                    parseFloat(i.price.amount),
                    i.quantity,
                    'checkout_backfill',
                    undefined,
                    i.variantId,
                  )),
                  `addtocart_backfill_${Date.now()}`,
                );
                untracked.forEach(i => sessionTrackedAtc.add(i.variantId));
              }
            } catch (err) {
              if (import.meta.env.DEV) console.warn('[Cart] add_to_cart backfill failed (non-blocking):', err);
            }

            // Track CRM event for begin_checkout (CRM only — not GA4)
            trackBeginCheckout({
              itemsCount: items.length,
              subtotal: getSubtotal(),
            });
            // NOTE: direct GA4 begin_checkout emission was REMOVED here.
            // The storefront now pushes the original `begin_checkout` event to
            // the dataLayer; GTM v41 pauses the GA4 tag, while Google Ads,
            // Meta InitiateCheckout and TikTok InitiateCheckout still trigger.
            // Shared event_id for Pixel <-> CAPI dedup (InitiateCheckout) —
            // generated BEFORE dlBeginCheckout so the same ID reaches the
            // dataLayer, the Pixel and the CAPI mirror.
            const initiateCheckoutEventId = `initiatecheckout_${Date.now()}`;
            dlBeginCheckout(
              items.map(i => shopifyNodeToItem(i.product.node, parseFloat(i.price.amount), i.quantity, undefined, undefined, i.variantId)),
              getSubtotal(),
              initiateCheckoutEventId,
            );
            // Meta Pixel InitiateCheckout
            mpInitiateCheckout({
              value: getSubtotal(),
              num_items: items.length,
              event_id: initiateCheckoutEventId,
            });
            // Server-side CAPI InitiateCheckout (mirror, fire-and-forget)
            void sendMetaCAPI({
              event_name: 'InitiateCheckout',
              event_id: initiateCheckoutEventId,
              custom_data: {
                value: getSubtotal(),
                currency: 'BRL',
                num_items: items.length,
                content_ids: items.map(i => shopifyNumericId(i.variantId)),
              },
            });
          }
          return checkoutUrl;
        } catch (error) {
          console.error('Failed to create checkout:', error);
          return null;
        } finally {
          setLoading(false);
        }
      },

      getSubtotal: () => {
        return get().items.reduce((sum, item) => 
          sum + (parseFloat(item.price.amount) * item.quantity), 0
        );
      },

      getItemCount: () => {
        return get().items.reduce((sum, item) => sum + item.quantity, 0);
      },
    }),
    {
      name: 'shopify-cart',
      // Bump when the persisted product snapshot shape/content must be invalidated.
      // v2 discards old carts that cached stale Shopify titles (e.g. KIT01 "5 peças"
      // vs the current "6 peças"), so titles always re-resolve from Shopify.
      version: 2,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ items: state.items }),
      migrate: (persistedState) => persistedState,

      onRehydrateStorage: () => (state) => {
        // After the saved cart loads, reconcile the gift line: add it back if
        // the trigger is present, or strip it if the promo was disabled.
        state?.reconcileGift();
      },
    }
  )
);
